/**
 * Helpers for the single-company subscription page (/ipo-subscription/:symbol).
 *
 * The NSE bid book (GET /api/market/subscription/{symbol}) is the core; the
 * aggregate subscription feed, the NSE live/upcoming schedules and the GMP
 * board add the company, price band, lot size and dates; the IPO catalog adds
 * whatever the extracted DRHP holds. Extracted DRHP fields are noisy, so each
 * is validated before it's shown.
 */
import { deriveBoard, parsePriceRange, toIsoDate } from './ipoCatalogAdapters';
import { addDays, isWeekend, nextBusinessDay, todayIso } from './ipoCalendar';
import { isSameCompany } from './smeIpos';
import { leadManagersFromDetail, stripExtractionLabels } from './drhpTracker';

const toNumber = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).replace(/,/g, '').trim();
  if (!text) return null;
  const number = Number(text);
  return Number.isFinite(number) ? number : null;
};

const firstText = (...values) => values.map((value) => (typeof value === 'string' ? value.trim() : '')).find(Boolean) || null;

const safeUrl = (value) => (/^https?:\/\//i.test(String(value || '')) ? value : null);

// --- Formatting ---------------------------------------------------------------

export const formatRupees = (value, digits = 0) =>
  value === null || value === undefined
    ? '—'
    : `₹${Number(value).toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;

export const formatCrore = (value) =>
  value === null || value === undefined ? '—' : `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr`;

export const formatTimes = (value) => (value === null || value === undefined ? '—' : `${value.toFixed(2)}x`);

export const formatCount = (value) => (value === null || value === undefined ? '—' : Math.round(value).toLocaleString('en-IN'));

// --- Bid book ------------------------------------------------------------------

const TOP_LEVEL = [
  { key: 'qib', pattern: /^qualified institutional buyers/i, label: 'QIB (Ex-Anchor)' },
  { key: 'nii', pattern: /^non institutional investors$/i, label: 'Non-Institutional (NII)' },
  { key: 'retail', pattern: /^(retail individual investors|individual investors)/i, label: 'Retail Individual (RII)', smeLabel: 'Individual Investors' },
  { key: 'employee', pattern: /^employee/i, label: 'Employee Reserved' },
  { key: 'shareholder', pattern: /^shareholder/i, label: 'Shareholder Reserved' },
  { key: 'policyholder', pattern: /^policy\s*holder/i, label: 'Policyholder Reserved' },
];

const NII_SPLIT = [
  { key: 'bnii', pattern: /more than ten lakh/i, label: 'bNII (Bids above ₹10 Lakh)' },
  { key: 'snii', pattern: /more than two lakh/i, label: 'sNII (Bids ₹2 – 10 Lakh)' },
];

const BREAKDOWN_LABELS = [
  [/foreign institutional/i, 'FIIs / FPIs'],
  [/domestic financial/i, 'Banks, FIs & Insurers'],
  [/mutual fund/i, 'Mutual Funds'],
  [/^corporates/i, 'Corporates'],
  [/^individuals/i, 'Individuals'],
  [/^cut\s*off/i, 'Cut-off bids'],
  [/^price bids/i, 'Price bids'],
  [/^others/i, 'Others'],
];

const cleanLabel = (text) => String(text || '').replace(/\s*\(.*$/, '').trim();

/**
 * NSE bid rows -> categories (with the NII split and sub-breakdowns) and total.
 * Rows are numbered "1", "2", "2.1", "3(a)"...: top-level categories carry a
 * whole number, the NII split a decimal and the breakdowns a letter.
 * Before bidding opens NSE returns only a zero "Total" row.
 *
 * @param {object[]} rows - data.data of GET /api/market/subscription/{symbol}
 * @param {object} options
 * @param {number} options.priceHigh - upper price band, for bid amounts
 * @param {'mainboard'|'sme'} options.board
 */
export const mapBidBook = (rows, { priceHigh = null, board = 'mainboard' } = {}) => {
  const list = (Array.isArray(rows) ? rows : []).filter((row) => row && row.category);
  const toEntry = (row, key, label) => {
    const offered = toNumber(row.noOfShareOffered);
    const bid = toNumber(row.noOfSharesBid);
    const reported = toNumber(row.noOfTotalMeant);
    return {
      key,
      label,
      offered,
      bid,
      times: reported ?? (offered && bid !== null ? bid / offered : null),
      amountCr: bid !== null && priceHigh ? (bid * priceHigh) / 1e7 : null,
    };
  };

  const categories = [];
  let total = null;
  list.forEach((row) => {
    const label = String(row.category).trim();
    const sr = row.srNo === null || row.srNo === undefined ? '' : String(row.srNo).trim();
    if (/^total$/i.test(label)) {
      total = toEntry(row, 'total', 'Total');
      return;
    }
    const split = NII_SPLIT.find((entry) => entry.pattern.test(label));
    if (split && (!sr || /^\d+\.\d+$/.test(sr))) {
      const parent = categories.find((category) => category.key === 'nii');
      if (parent) parent.children.push(toEntry(row, split.key, split.label));
      return;
    }
    const top = TOP_LEVEL.find((entry) => entry.pattern.test(label));
    if (top && (!sr || /^\d+$/.test(sr))) {
      const name = board === 'sme' && top.smeLabel ? top.smeLabel : top.label;
      categories.push({ ...toEntry(row, top.key, name), sr, children: [], breakdown: [] });
      return;
    }
    if (/^\d+$/.test(sr)) {
      categories.push({ ...toEntry(row, `cat-${sr}`, cleanLabel(label) || label), sr, children: [], breakdown: [] });
      return;
    }
    // "1(a) FIIs", "3(a) Cut Off": breakdowns of a top-level category
    const breakdownOf = sr.match(/^(\d+)\([a-z]\)$/i);
    const parent = breakdownOf && categories.find((category) => category.sr === breakdownOf[1]);
    if (parent) {
      const [, name] = BREAKDOWN_LABELS.find(([pattern]) => pattern.test(label)) || [null, cleanLabel(label) || label];
      parent.breakdown.push({ label: name, bid: toNumber(row.noOfSharesBid) });
    }
  });

  const hasBids = Boolean(total?.bid) || categories.some((category) => category.bid > 0);
  const retail = categories.find((category) => category.key === 'retail');
  const bidsOf = (name) => retail?.breakdown.find((entry) => entry.label === name)?.bid ?? null;
  const [cutOff, priceBids] = [bidsOf('Cut-off bids'), bidsOf('Price bids')];

  return {
    categories,
    total,
    hasBids,
    // Share of retail bids placed at the cut-off price rather than a fixed price
    retailCutOffShare: cutOff !== null && priceBids !== null && cutOff + priceBids > 0 ? cutOff / (cutOff + priceBids) : null,
  };
};

/**
 * Each category's share of the shares offered for bidding (the public book,
 * excluding anchors), with the ₹ value at the upper price band.
 */
export const buildQuotaAllocation = (book, priceHigh = null) => {
  const offered = book?.total?.offered || (book?.categories || []).reduce((sum, category) => sum + (category.offered || 0), 0);
  if (!offered) return [];
  const share = (entry) => ({
    key: entry.key,
    label: entry.label,
    offered: entry.offered,
    percent: (entry.offered / offered) * 100,
    amountCr: priceHigh ? (entry.offered * priceHigh) / 1e7 : null,
  });
  return book.categories
    .filter((category) => category.offered)
    .map((category) => ({ ...share(category), children: category.children.filter((child) => child.offered).map(share) }));
};

// --- Lots & bidding ----------------------------------------------------------------

const RETAIL_CAP = 200000;
const SHNI_CAP = 1000000;

/**
 * Application slabs at the upper price band. Mainboard retail bids up to
 * ₹2 lakh; small HNIs above that up to ₹10 lakh; big HNIs beyond. SME
 * individual investors apply for two lots and HNIs for three or more (SEBI
 * ICDR, 2025).
 */
export const buildLotSlabs = ({ lotSize, price, board = 'mainboard' }) => {
  if (!lotSize || !price) return [];
  const lotValue = lotSize * price;
  const slab = (key, label, tier, lots) => ({ key, label, tier, lots, shares: lots * lotSize, amount: lots * lotValue });

  if (board === 'sme') {
    const shniMax = Math.max(3, Math.floor(SHNI_CAP / lotValue));
    return [
      slab('individual', 'Individual (Min)', 'retail', 2),
      slab('shni-min', 'Small HNI (Min)', 'shni', 3),
      slab('shni-max', 'Small HNI (Max)', 'shni', shniMax),
      slab('bhni-min', 'Big HNI (Min)', 'bhni', shniMax + 1),
    ];
  }
  const retailMax = Math.max(1, Math.floor(RETAIL_CAP / lotValue));
  const shniMax = Math.max(retailMax + 1, Math.floor(SHNI_CAP / lotValue));
  return [
    slab('retail-min', 'Retail (Min)', 'retail', 1),
    slab('retail-max', 'Retail (Max)', 'retail', retailMax),
    slab('shni-min', 'Small HNI (Min)', 'shni', retailMax + 1),
    slab('shni-max', 'Small HNI (Max)', 'shni', shniMax),
    slab('bhni-min', 'Big HNI (Min)', 'bhni', shniMax + 1),
  ];
};

// Lot range per bidder tier, from buildLotSlabs
export const lotRange = (slabs, tier) => {
  const lots = slabs.filter((slab) => slab.tier === tier).map((slab) => slab.lots);
  return lots.length ? { min: Math.min(...lots), max: Math.max(...lots) } : null;
};

// --- Schedule ----------------------------------------------------------------------

const previousBusinessDay = (iso) => {
  let previous = addDays(iso, -1);
  while (isWeekend(previous)) previous = addDays(previous, -1);
  return previous;
};

export const issuePhase = ({ openOn, closeOn }, today = todayIso()) => {
  if (!openOn && !closeOn) return 'unknown';
  if (openOn && today < openOn) return 'upcoming';
  if (closeOn && today > closeOn) return 'closed';
  if (closeOn && today === closeOn) return 'closing_today';
  return 'open';
};

/**
 * Five milestones from bidding to listing. Missing post-issue dates follow
 * SEBI's T+3 timeline in working days (allotment T+1, refunds and demat
 * credit T+2, listing T+3) and are flagged as estimates; exchange holidays
 * aren't known here.
 */
export const buildIssueSchedule = ({ openOn, closeOn, boaOn, listingOn, registrar, listingVenue }, today = todayIso()) => {
  const allotmentOn = boaOn || (closeOn ? nextBusinessDay(closeOn) : null);
  const refundOn = allotmentOn ? nextBusinessDay(allotmentOn) : null;
  const listOn = listingOn || (refundOn ? nextBusinessDay(refundOn) : null);
  const steps = [
    { key: 'open', label: 'Bidding Opens', date: openOn, note: '10:00 AM IST', estimated: false },
    { key: 'close', label: 'Bidding Closes', date: closeOn, note: 'UPI cut-off 5:00 PM IST', estimated: false },
    { key: 'allotment', label: 'Basis of Allotment', date: allotmentOn, note: registrar ? `${registrar} registry` : 'Registrar', estimated: !boaOn },
    { key: 'refund', label: 'Refunds & Demat Credit', date: refundOn, note: 'Credit to demat', estimated: true },
    { key: 'listing', label: `${listingVenue || 'Exchange'} Listing`, date: listOn, note: 'Trading from 10:00 AM', estimated: !listingOn },
  ];
  return steps.map((step) => {
    let state = 'upcoming';
    if (step.date && step.key === 'open') {
      if (today >= step.date) state = closeOn && closeOn !== step.date && today >= closeOn ? 'done' : 'active';
    } else if (step.date) {
      if (today > step.date) state = 'done';
      else if (today === step.date) state = 'active';
    }
    return { ...step, state };
  });
};

// Working day of the bidding window (Day 1, Day 2, ...), null before it opens
export const biddingDay = ({ openOn, closeOn }, today = todayIso()) => {
  if (!openOn || today < openOn) return null;
  const last = closeOn && today > closeOn ? closeOn : today;
  let day = 0;
  for (let date = openOn; date <= last; date = addDays(date, 1)) {
    if (!isWeekend(date)) day += 1;
  }
  return Math.max(day, 1);
};

const atIst = (iso, hhmm) => new Date(`${iso}T${hhmm}:00+05:30`).getTime();

export const formatCountdown = (ms) => {
  if (!(ms > 0)) return null;
  const minutes = Math.floor(ms / 60000);
  const pad = (n) => String(n).padStart(2, '0');
  const [days, hours, mins] = [Math.floor(minutes / 1440), Math.floor((minutes % 1440) / 60), minutes % 60];
  return days ? `${days}d ${pad(hours)}h ${pad(mins)}m` : `${pad(hours)}h ${pad(mins)}m`;
};

// Bidding runs 10:00 AM to 5:00 PM IST
export const describeBiddingWindow = ({ openOn, closeOn }, now = new Date()) => {
  const time = now.getTime();
  const opensAt = openOn ? atIst(openOn, '10:00') : null;
  const closesAt = closeOn ? atIst(closeOn, '17:00') : null;
  if (opensAt && time < opensAt) return { tone: 'upcoming', text: `Book-building window opens in ${formatCountdown(opensAt - time)}` };
  if (closesAt && time < closesAt) return { tone: 'live', text: `Book-building window closes in ${formatCountdown(closesAt - time)}` };
  if (closesAt) return { tone: 'closed', text: 'Book-building window has closed' };
  return { tone: 'unknown', text: 'Bidding dates awaited' };
};

/**
 * Anchor book dates under SEBI ICDR: anchors bid one working day before the
 * issue opens; half their shares are locked in for 30 days from allotment and
 * the rest for 90 days.
 */
export const anchorSchedule = ({ openOn, allotmentOn }) => ({
  bidOn: openOn ? previousBusinessDay(openOn) : null,
  lockIn30On: allotmentOn ? addDays(allotmentOn, 30) : null,
  lockIn90On: allotmentOn ? addDays(allotmentOn, 90) : null,
});

// --- Issue resolution -----------------------------------------------------------------

const sameSymbol = (value, symbol) => String(value || '').trim().toUpperCase() === symbol;

/**
 * Company, board, dates, price band, lot size and GMP for a symbol, from
 * whichever market feed lists it. Null when no feed knows the symbol.
 *
 * @param {object} data
 * @param {object[]} data.subscriptionRows - selectSubscriptionRows + selectSmeSubscriptionRows
 * @param {object[]} data.liveItems - raw NSE items of GET /api/market/ipos/live (current + upcoming)
 * @param {object[]} data.scheduleRows - selectOpeningSoonIpos + selectSmeOpeningSoonIpos
 * @param {object[]} data.gmpRows - GMP board rows (mainboard + SME)
 */
export const resolveIssue = ({ symbol, subscriptionRows = [], liveItems = [], scheduleRows = [], gmpRows = [] }) => {
  const code = String(symbol || '').trim().toUpperCase();
  if (!code) return null;
  const summary = subscriptionRows.find((row) => sameSymbol(row?.symbol, code)) || null;
  const live = liveItems.find((item) => sameSymbol(item?.symbol, code)) || null;
  const schedule = scheduleRows.find((row) => sameSymbol(row?.symbol, code)) || null;
  const company = firstText(summary?.company, live?.companyName, schedule?.company);
  if (!company) return null;

  const gmp = gmpRows.find((row) => isSameCompany(row.name, company)) || null;
  const band = [summary?.priceBand, live?.issuePrice, live?.priceBand, schedule?.priceBand]
    .map((text) => parsePriceRange(text || ''))
    .find((range) => range.high) || {};
  const priceHigh = band.high ?? gmp?.price ?? null;
  const lotSize = schedule?.lotSize || toNumber(live?.lotSize) || gmp?.lotSize || null;
  let board = summary?.board || schedule?.board || (live?.series ? deriveBoard(live.series) : null);
  if (!board) board = gmp?.board === 'SME' ? 'sme' : 'mainboard';

  return {
    symbol: code,
    company,
    board,
    series: firstText(live?.series) || (board === 'sme' ? 'SME' : 'EQ'),
    openOn: summary?.openOn || toIsoDate(live?.issueStartDate) || schedule?.openOn || gmp?.openOn || null,
    closeOn: summary?.closeOn || toIsoDate(live?.issueEndDate) || schedule?.closeOn || gmp?.closeOn || null,
    boaOn: gmp?.boaOn || null,
    listingOn: gmp?.listingOn || null,
    priceLow: band.low ?? priceHigh,
    priceHigh,
    lotSize,
    minInvestment: lotSize && priceHigh ? lotSize * priceHigh : null,
    sizeCr: gmp?.sizeCr ?? null,
    gmp,
  };
};

// --- Issuer profile from the extracted DRHP -------------------------------------------------

const CIN_PATTERN = /^[LU]\d{5}[A-Z]{2}\d{4}[A-Z]{3}\d{6}$/;
const PIN_CODE = /\b\d{3}\s?\d{3}\b/;
const PERCENT_TEXT = /^-?\d+(?:\.\d+)?\s*%?$/;

// Keeps an address up to its PIN code (and a trailing "State, India"); drops fragments
export const cleanAddress = (text) => {
  if (typeof text !== 'string') return null;
  const flat = text.replace(/\s+/g, ' ').trim();
  const pin = flat.match(PIN_CODE);
  if (!pin) return null;
  let end = pin.index + pin[0].length;
  const tail = flat.slice(end).match(/^,?\s*[A-Za-z ]{3,30},\s*India\b/);
  if (tail) end += tail[0].length;
  const address = flat.slice(0, end).replace(/[\s,;:–-]+$/, '');
  return address.length >= 20 ? address : null;
};

// Latest financial year with a plausible percentage ("11.55%"), e.g. { value: 11.55, year: 'FY25' }
const latestPercent = (byYear) => {
  const entries = Object.entries(byYear || {})
    .filter(([, value]) => typeof value === 'string' && PERCENT_TEXT.test(value.replace(/,/g, '').trim()))
    .sort(([a], [b]) => b.localeCompare(a));
  for (const [year, value] of entries) {
    const number = parseFloat(value.replace(/,/g, ''));
    if (Math.abs(number) <= 100) return { value: number, year: `FY${year.replace(/\D/g, '').slice(-2)}` };
  }
  return null;
};

const KPI_METRICS = [
  { key: 'roe_percent', label: 'ROE', note: 'Return on equity' },
  { key: 'ronw_percent', label: 'RoNW', note: 'Return on net worth' },
  { key: 'roce_percent', label: 'ROCE', note: 'Return on capital employed' },
  { key: 'ebitda_margin', label: 'EBITDA Margin', note: 'Operating margin' },
  { key: 'pat_margin_percent', label: 'PAT Margin', note: 'Net profit margin' },
];

const positiveNumber = (value) => {
  const number = toNumber(typeof value === 'string' ? value.replace(/[^\d.,-]/g, '') : value);
  return number && number > 0 ? number : null;
};

const REGISTRAR_PORTALS = [
  [/kfin/i, 'https://ipostatus.kfintech.com/'],
  [/link intime|mufg/i, 'https://in.mpms.mufg.com/Initial_Offer/public-issues.html'],
  [/bigshare/i, 'https://ipo.bigshareonline.com/ipo_status.html'],
  [/skyline/i, 'https://www.skylinerta.com/ipo.php'],
  [/maashitla/i, 'https://maashitla.com/allotment-status/public-issues'],
  [/cameo/i, 'https://ipostatus1.cameoindia.com/'],
  [/purva/i, 'https://www.purvashare.com/investor-service/ipo-query'],
];
// BSE's allotment status page covers every registrar
export const EXCHANGE_ALLOTMENT_URL = 'https://www.bseindia.com/investors/appli_check.aspx';

export const registrarPortal = (name) => (REGISTRAR_PORTALS.find(([pattern]) => pattern.test(name || '')) || [])[1] || null;

/**
 * The parts of a mapIpoDetailResponse record worth showing, each validated;
 * null for an issuer the catalog doesn't have.
 */
export const buildIssuerProfile = (detail) => {
  if (!detail) return null;
  const json = detail.raw?.json_data || {};
  const structure = json.ipo_structure || {};

  const registrarName = stripExtractionLabels(detail.registrar?.name);
  const registrar = registrarName.length > 3
    ? {
        name: registrarName,
        email: /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(detail.registrar?.email || '') ? detail.registrar.email : null,
        phone: /\d{6,}/.test(String(detail.registrar?.telephone || '').replace(/\D/g, '')) ? detail.registrar.telephone.trim() : null,
        portal: registrarPortal(registrarName),
      }
    : null;

  const kpis = KPI_METRICS.map((metric) => {
    const latest = latestPercent(detail.kpis?.[metric.key]);
    return latest ? { ...metric, ...latest } : null;
  }).filter(Boolean);

  const holders = (detail.promoterShareholding || [])
    .map((holder) => ({
      name: stripExtractionLabels(holder?.name),
      percent: parseFloat(String(holder?.percentage || '').replace(/[^\d.]/g, '')),
    }))
    .filter((holder) => holder.name && holder.percent > 0 && holder.percent <= 100);
  const promoterTotal = holders.reduce((sum, holder) => sum + holder.percent, 0);

  const objects = (json.objects_of_issue?.proposed_utilisation || [])
    .map((item) => {
      const object = firstText(item?.object, item?.purpose);
      if (!object || object.length < 6) return null;
      let amount = null;
      if (item.amount_lakhs) amount = `₹${item.amount_lakhs} lakh`;
      else if (item.amount) amount = `₹${item.amount}`;
      return { object, amount };
    })
    .filter(Boolean)
    .slice(0, 6);

  const facilities = [].concat(detail.manufacturing || []).filter((text) => typeof text === 'string' && text.trim()).map((text) => text.trim());
  const products = (detail.productCategories || []).filter((text) => typeof text === 'string' && text.trim().length > 1 && text.length <= 60);
  const presence = typeof detail.geographicPresence === 'string' && detail.geographicPresence.length <= 240 ? detail.geographicPresence.trim() : null;

  const valuation = detail.raw?.json_data?.derived_information?.valuation || {};
  const faceValue = positiveNumber(structure.face_value_per_share);
  const cin = String(detail.cin || '').trim().toUpperCase();

  return {
    logoUrl: detail.logoUrl || null,
    faceValue,
    issueType: firstText(detail.issueType),
    leadManagers: leadManagersFromDetail(detail),
    registrar,
    registeredOffice: cleanAddress(detail.registeredOffice),
    cin: CIN_PATTERN.test(cin) ? cin : null,
    tags: products.filter((text) => text.length <= 30).slice(0, 3),
    kpis,
    peIssue: positiveNumber(valuation.pe_at_issue_price),
    mcapPreCr: positiveNumber(valuation.market_cap_pre_issue_cr),
    mcapPostCr: positiveNumber(valuation.market_cap_post_issue_cr),
    promoterHolding: holders.length && promoterTotal <= 100 ? { total: promoterTotal, holders: holders.slice(0, 4) } : null,
    objects,
    operations: { facilities, products: products.slice(0, 8), presence },
  };
};

// --- Broker consensus ------------------------------------------------------------------

const callBucket = (call) => {
  if (/avoid|not subscribe/i.test(call)) return 'avoid';
  if (/caution/i.test(call) || !/subscribe|apply|buy/i.test(call)) return 'neutral';
  return 'subscribe';
};

// GET /api/core/ipos/{id}/broker-reviews -> data
export const summarizeBrokerReviews = (payload) => {
  const reviews = (Array.isArray(payload?.reviews) ? payload.reviews : [])
    .map((review) => ({
      name: firstText(review?.broker_name, review?.broker, review?.analyst),
      call: firstText(review?.rating, review?.call, review?.recommendation) || 'Not Rated',
      url: safeUrl(review?.source_url || review?.url || review?.report_url),
    }))
    .filter((review) => review.name);
  const counts = { subscribe: 0, neutral: 0, avoid: 0 };
  reviews.forEach((review) => {
    counts[callBucket(review.call)] += 1;
  });
  return { total: reviews.length, counts, reviews };
};

// --- FAQs --------------------------------------------------------------------------------

const shortCompany = (company) => company.replace(/\s+(Ltd\.?|Limited)$/i, '').trim();

export const buildIssueFaqs = ({ issue, slabs = [], schedule = [], profile = null, formatDate = (iso) => iso }) => {
  const name = `${shortCompany(issue.company)} IPO`;
  const step = (key) => schedule.find((entry) => entry.key === key) || {};
  const faqs = [];

  if (issue.priceHigh && issue.lotSize) {
    const band = issue.priceLow && issue.priceLow !== issue.priceHigh ? `₹${issue.priceLow} to ₹${issue.priceHigh}` : `₹${issue.priceHigh}`;
    const retail = lotRange(slabs, 'retail');
    const retailText =
      issue.board === 'sme'
        ? `Individual investors apply for ${retail?.min || 2} lots (${formatRupees((retail?.min || 2) * issue.lotSize * issue.priceHigh)}).`
        : `Retail investors can bid for ${retail?.min} to ${retail?.max} lots, up to ₹2 lakh.`;
    faqs.push({
      question: `What is the price band and minimum investment for ${name}?`,
      answer: `The price band is ${band} per share and one lot is ${issue.lotSize.toLocaleString('en-IN')} shares, so a lot costs ${formatRupees(issue.minInvestment)} at the upper band. ${retailText}`,
    });
  }

  if (issue.closeOn) {
    const allotment = step('allotment');
    const listing = step('listing');
    const estimated = allotment.estimated || listing.estimated ? ' (tentative, per the T+3 timeline)' : '';
    faqs.push({
      question: `When does bidding for ${name} close and when is the allotment date?`,
      answer: `Bidding closes on ${formatDate(issue.closeOn)} and UPI mandates must be approved by 5:00 PM IST. The basis of allotment is expected on ${formatDate(allotment.date)} and listing on ${formatDate(listing.date)}${estimated}.`,
    });
  }

  faqs.push({
    question: `How do I apply for ${name} through UPI ASBA?`,
    answer: `Place a bid in your broker's IPO section (Zerodha, Groww, Angel One and others) or through your bank's ASBA net-banking, enter your UPI ID, and approve the mandate in your UPI app. The amount stays blocked in your account until allotment and is released if no shares are allotted.`,
  });

  faqs.push({
    question: `How is the ${name} subscription figure calculated?`,
    answer: `Each category's multiple is the shares bid divided by the shares offered to it. The anchor portion is allotted before the issue opens, so it's excluded from the public book. Figures come from the exchange's live bid book and are refreshed through the bidding day.`,
  });

  if (profile?.objects?.length) {
    faqs.push({
      question: `How will the ${name} proceeds be used?`,
      answer: profile.objects.map((item) => (item.amount ? `${item.object} (${item.amount})` : item.object)).join('; ') + '.',
    });
  }

  if (profile?.registrar) {
    faqs.push({
      question: `How can I check the ${name} allotment status?`,
      answer: `${profile.registrar.name} is the registrar. Once the basis of allotment is finalised, check your status on the registrar's portal or on BSE with your PAN or application number.`,
    });
  }

  return faqs;
};
